import {
  getMergeAffectedUrl,
  getMergeDuplicatesQueryKey,
  getMergeQueryLinesQueryKey,
  mergeListPreview,
  mergePreview,
  useMergeDuplicates,
  useMergeListRun,
  useMergeQueryLines,
  useMergeRun,
} from "@api/generated/merge/merge";
import {
  type Affected,
  type DuplicateGroup,
  type Duplicates,
  type LineKey,
  type ListRow,
  type MergeDraft,
  type MergeDraftSource,
  MergeDuplicatesKey,
  type MergePlan,
} from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { usePreview } from "@app/preview";
import { hasRole, useAutosave, useSettings } from "@app/workspace";
import { keyParam, keyText } from "@features/genealogy/genotype";
import { queryListText } from "@features/genealogy/mergeList";
import { SavedQuerySelect, useSavedQuery } from "@features/queries/SavedQuerySelect";
import { QueryFeedback } from "@ui/QueryFeedback";
import "@features/genealogy/genealogy.css";
import { LinePicker } from "@features/genealogy/GenotypeParts";
import { Button, Checkbox, FileButton, Loader, Modal, Radio, SegmentedControl, Select, Textarea, TextInput } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatNumber } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { RemovableChip } from "@ui/RemovableChip";
import { Splitter } from "@ui/Splitter";
import { codeColumn } from "@ui/table/columns";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const KEYS = Object.values(MergeDuplicatesKey);
const FIELDS = [
  "fajta",
  "torzs_nev",
  "rov",
  "torzs_szam",
  "pedigre",
  "pgm",
  "gh",
  "orsz",
  "ceg",
  "reg",
  "canc",
  "mta_gene_bank",
  "torzs_megj",
] as const;
type Field = (typeof FIELDS)[number];

export const Route = createFileRoute("/genealogy/merge")({
  validateSearch: z.object({
    key: z.enum(MergeDuplicatesKey).optional(),
    any_pgm: z.boolean().optional(),
    g: z.number().int().min(0).optional(),
  }),
  component: MergePage,
});

const same = (a: LineKey, b: LineKey) => a.paz === b.paz && a.sid === b.sid;
const blank = (v: unknown) => v == null || (typeof v === "string" && !v.trim());
const shown = (v: unknown) => (blank(v) ? "—" : String(v));

/** Genealógia → Összevonás (the legacy Genealogy: Simple Merge, Duplicates, Transpose from file): several register
 *  lines of one genotype merged into a target field by field, every reference in every year repointed; or a list of
 *  old → new keys. The candidates are the duplicate groups or a saved query's lines in its order (the legacy Query
 *  closed on the Genealogy window filled its source grid, the rows clicked went onto the merge). Admin only;
 *  previewed, confirmed by typing the target's name, undoable from the Napló. */
function MergePage() {
  const { t } = useTranslation();
  const me = useUserMe().data;
  const settings = useSettings();
  if (!me || !settings) return <Loader size="sm" m="md" />;
  if (!hasRole(me.roles, "admin")) {
    return (
      <div className="page">
        <PageHeader title={t("merge.title")} />
        <p className="mu">{t("merge.noPermission")}</p>
      </div>
    );
  }
  return <Merge stored={settings.merge_draft ?? null} />;
}

/** Keys handed to the board from outside (the query's lines): ``n`` counts the hand-overs, so the same keys twice
 *  are added twice. */
type Incoming = { n: number; keys: LineKey[] };

function Merge({ stored }: { stored: MergeDraft | null }) {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [init] = useState(() => stored);
  const [source, setSource] = useState<MergeDraftSource>(init?.source ?? "duplicates");
  const [query, setQuery] = useState<number | null>(init?.query ?? null);
  const draft = useMemo<MergeDraft>(() => ({ source, query }), [source, query]);
  useAutosave("merge_draft", draft, init);
  const [incoming, setIncoming] = useState<Incoming>({ n: 0, keys: [] });
  const byQuery = source === "query";
  const key = search.key ?? "fajta";
  const anyPgm = search.any_pgm ?? false;
  const groups = useMergeDuplicates({ key, any_pgm: anyPgm }, { query: { enabled: !byQuery } }).data;
  const [listing, setListing] = useState(false);
  const index = search.g ?? 0;
  const group = byQuery ? undefined : groups?.groups[Math.min(index, (groups?.groups.length ?? 1) - 1)];
  const pick = (g: number) => void navigate({ search: (s) => ({ ...s, g }) });
  useHotkeys([["n", () => !byQuery && groups && index + 1 < groups.groups.length && pick(index + 1)]]);
  const hand = (keys: LineKey[]) => setIncoming((x) => ({ n: x.n + 1, keys }));
  return (
    <div className="page fill">
      <PageHeader
        title={t("merge.title")}
        subtitle={t("merge.subtitle")}
        actions={
          <div className="hstack" style={{ gap: 8 }}>
            <span className="chip r">{t("merge.adminOnly")}</span>
            <Button size="compact-sm" variant="default" onClick={() => setListing(true)}>
              {t("merge.fromList")}
            </Button>
          </div>
        }
      />
      <div className="fill-row">
        <aside className="card stack mg-groups">
          <SegmentedControl
            size="xs"
            fullWidth
            aria-label={t("merge.candidates")}
            value={source}
            onChange={(v) => {
              setSource(v as MergeDraftSource);
              setIncoming({ n: 0, keys: [] }); // a board opened later does not take the last hand-over again
            }}
            data={(["duplicates", "query"] as const).map((s) => ({ value: s, label: t(`merge.candidateMode.${s}`) }))}
          />
          {byQuery ? (
            <QueryCandidates query={query} onQuery={setQuery} onPick={hand} />
          ) : (
            <DuplicateList groups={groups} group={group} pick={pick} keyName={key} anyPgm={anyPgm} />
          )}
        </aside>
        <Splitter cssVar="--mg-groups-w" side="start" min={180} max={480} />
        <Board
          key={byQuery ? "query" : group ? `${key}·${group.value}·${group.pgm}` : "manual"}
          initial={group?.keys ?? []}
          incoming={incoming}
        />
      </div>
      <ListModal opened={listing} onClose={() => setListing(false)} query={query} />
    </div>
  );
}

/** The duplicate groups of a register field (the legacy Duplicates): a group opens on the board. */
function DuplicateList(props: {
  groups: Duplicates | undefined;
  group: DuplicateGroup | undefined;
  pick: (g: number) => void;
  keyName: MergeDuplicatesKey;
  anyPgm: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const { groups, group, pick } = props;
  const key = props.keyName;
  const anyPgm = props.anyPgm;
  return (
    <>
      <span className="sec">{t("merge.findDuplicates")}</span>
      <Select
        size="xs"
        aria-label={t("merge.matchKey")}
        value={key}
        allowDeselect={false}
        onChange={(v) => v && void navigate({ search: (s) => ({ ...s, key: v as MergeDuplicatesKey, g: undefined }) })}
        data={KEYS.map((k) => ({ value: k, label: t(`merge.matchKeys.${k}`) }))}
      />
      <Checkbox
        size="xs"
        label={t("merge.differentPgm")}
        checked={anyPgm}
        disabled={key === "pedigre"}
        onChange={(e) => void navigate({ search: (s) => ({ ...s, any_pgm: e.currentTarget.checked || undefined, g: undefined }) })}
      />
      <div className="scroll grow stack" style={{ gap: 2 }}>
        {groups?.groups.map((g, i) => (
          <button type="button" key={`${g.value}·${g.pgm ?? ""}`} className={`mg-group${g === group ? " on" : ""}`} onClick={() => pick(i)}>
            <span className="grow">{g.value}</span>
            {g.pgm && <span className="sm mu">{g.pgm}</span>}
            <span className="chip">{g.keys.length}</span>
          </button>
        ))}
        {groups && !groups.groups.length && <span className="sm mu">{t("merge.noGroups")}</span>}
      </div>
      {groups && (
        <span className="sm mu">
          {t("merge.groupCount", { count: formatNumber(groups.groups.length) })}
          {groups.truncated && ` (${t("merge.truncated", { count: formatNumber(groups.groups.length) })})`} · <span className="kbd">N</span>{" "}
          {t("merge.next")}
        </span>
      )}
    </>
  );
}

/** A saved query's lines in its order as the candidates (the legacy source grid the Query filled): a click puts one
 *  on the board (the first becomes the target), or all of them at once. */
function QueryCandidates(props: { query: number | null; onQuery: (id: number | null) => void; onPick: (keys: LineKey[]) => void }) {
  const { t } = useTranslation();
  const saved = useSavedQuery(props.query);
  const lines = useMergeQueryLines(
    { query: props.query ?? 0 },
    {
      query: {
        enabled: props.query != null && saved.ready,
        queryKey: [...getMergeQueryLinesQueryKey({ query: props.query ?? 0 }), saved.stamp],
      },
    },
  );
  const found = lines.data;
  return (
    <>
      <SavedQuerySelect value={props.query} onChange={(id) => props.onQuery(id)} w="100%" required description={t("merge.queryHelp")} />
      <QueryFeedback busy={lines.isFetching} error={lines.error} retry={lines.refetch} />
      <div className="scroll grow stack" style={{ gap: 2 }}>
        {found?.lines.map((r) => {
          const k = { paz: r.paz, sid: r.sid };
          return (
            <button type="button" key={keyText(k)} className="mg-group" onClick={() => props.onPick([k])}>
              <span className="mono sm">{keyText(k)}</span>
              <span className="grow">{r.name ?? "—"}</span>
              {r.pgm && <span className="sm mu">{r.pgm}</span>}
            </button>
          );
        })}
        {found && !found.lines.length && <span className="sm mu">{t("merge.queryEmpty")}</span>}
      </div>
      {found && found.lines.length > 0 && (
        <div className="hstack" style={{ gap: 8, flexWrap: "wrap" }}>
          <span className="sm mu grow">
            {t("merge.queryCount", { count: formatNumber(found.lines.length) })}
            {found.missing > 0 && ` · ${t("merge.queryMissing", { count: formatNumber(found.missing) })}`}
          </span>
          <Button size="compact-xs" variant="default" onClick={() => props.onPick(found.lines.map((r) => ({ paz: r.paz, sid: r.sid })))}>
            {t("merge.addAllToTable")}
          </Button>
        </div>
      )}
    </>
  );
}

/** After a merge: the duplicate groups and a saved query's candidates (the merged lines are gone from both). */
function useInvalidateDuplicates() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: [getMergeDuplicatesQueryKey()[0]] });
    void client.invalidateQueries({ queryKey: [getMergeQueryLinesQueryKey()[0]] });
  };
}

type Member = { key: LineKey; merged: boolean }; // merged: goes into the target (the target's own flag is ignored)

const MAX_MEMBERS = 201; // a target and the API's 200 merged lines

/** The lines on the board: a target, the ones merged into it, and the field-by-field choices; ``incoming`` keys
 *  (a saved query's candidates) are added as they come. */
function Board({ initial, incoming }: { initial: LineKey[]; incoming?: Incoming }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState<Member[]>(initial.map((key) => ({ key, merged: true })));
  const [target, setTarget] = useState<LineKey | undefined>(initial[0]);
  const [choices, setChoices] = useState<Partial<Record<Field, LineKey>>>({});
  const [confirm, setConfirm] = useState("");
  const invalidate = useInvalidateDuplicates();
  const sources = target ? members.filter((m) => m.merged && !same(m.key, target)).map((m) => m.key) : [];
  const body = target && sources.length ? { target, sources, choices } : null;
  const preview = usePreview("merge-preview", body, (b, signal) => mergePreview(b, { signal }));
  const plan = preview.data;
  const run = useMergeRun({
    mutation: {
      onSuccess: (out) => {
        notifications.show({ color: "green", message: t("merge.done", { count: out.written }) });
        setMembers(members.filter((m) => !sources.some((k) => same(k, m.key)))); // the merged lines are gone
        setConfirm("");
        invalidate();
      },
    },
  });
  const add = (key: LineKey) => addAll([key]);
  const addAll = (keys: LineKey[]) => {
    const fresh = keys.filter((k, i) => !members.some((m) => same(m.key, k)) && keys.findIndex((x) => same(x, k)) === i);
    const room = Math.max(0, MAX_MEMBERS - members.length);
    if (fresh.length > room) {
      notifications.show({
        color: "yellow",
        message: t("merge.tableFull", { count: formatNumber(room), skipped: formatNumber(fresh.length - room), max: MAX_MEMBERS }),
      });
    }
    const added = fresh.slice(0, room);
    setMembers([...members, ...added.map((key) => ({ key, merged: true }))]);
    setTarget((now) => now ?? added[0]);
  };
  const handed = incoming?.n ?? 0;
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new hand-over (its count) adds its keys, once
  useEffect(() => {
    if (handed && incoming?.keys.length) addAll(incoming.keys);
  }, [handed]);
  const remove = (key: LineKey) => {
    const rest = members.filter((m) => !same(m.key, key));
    setMembers(rest);
    if (target && same(target, key)) setTarget(rest[0]?.key);
  };
  const toggle = (key: LineKey) => setMembers(members.map((m) => (same(m.key, key) ? { ...m, merged: !m.merged } : m)));
  return (
    <>
      <div className="grow stack" style={{ gap: 14, minWidth: 0, overflowY: "auto" }}>
        <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
        <section className="card" style={{ overflow: "hidden" }}>
          <div className="hstack panel-head">
            <h2 className="h2 grow">{t("merge.buildTarget")}</h2>
            <span className="sm mu">{t("merge.buildTargetHelp")}</span>
            <div style={{ width: 260 }}>
              <LinePicker label={t("merge.addLine")} onPick={add} />
            </div>
          </div>
          {members.length ? (
            <FieldTable
              members={members}
              target={target}
              plan={plan}
              choices={choices}
              onTarget={setTarget}
              onToggle={toggle}
              onRemove={remove}
              onChoose={(f, key) => setChoices({ ...choices, [f]: key })}
            />
          ) : (
            <p className="sm mu inset">{t("merge.empty")}</p>
          )}
        </section>
        {plan && target && (
          <section className="card pad stack" style={{ gap: 10 }}>
            <h2 className="h2">{t("merge.confirmTitle")}</h2>
            <span className="sm">{t("merge.confirmText", { source: sources.map(keyText).join(", "), target: keyText(target) })}</span>
            <div className="hstack" style={{ gap: 10, alignItems: "flex-end" }}>
              <TextInput
                className="grow"
                size="sm"
                label={t("merge.confirmLabel", { text: plan.confirm })}
                value={confirm}
                onChange={(e) => setConfirm(e.currentTarget.value)}
              />
              <Button
                color="red"
                variant="outline"
                disabled={!body || confirm.trim().toUpperCase() !== plan.confirm.trim().toUpperCase()}
                loading={run.isPending}
                onClick={() => body && run.mutate({ data: { ...body, token: plan.token, confirm } })}
              >
                {t("merge.run")}
              </Button>
            </div>
          </section>
        )}
      </div>
      <aside className="card pad stack mg-side">
        <h2 className="h2">{t("merge.affected")}</h2>
        {plan && <AffectedList affected={plan.affected} pedigrees={plan.pedigrees.length} removed={plan.removed.length} />}
        <span className="grow" />
        {plan && target && (
          <a className="sm" href={getMergeAffectedUrl({ target: keyParam(target), sources: sources.map(keyParam) })}>
            {t("merge.itemisedList")}
          </a>
        )}
      </aside>
    </>
  );
}

function FieldTable(props: {
  members: Member[];
  target: LineKey | undefined;
  plan: MergePlan | undefined;
  choices: Partial<Record<Field, LineKey>>;
  onTarget: (k: LineKey) => void;
  onToggle: (k: LineKey) => void;
  onRemove: (k: LineKey) => void;
  onChoose: (f: Field, k: LineKey) => void;
}) {
  const { t } = useTranslation();
  const { plan } = props;
  const records = new Map((plan?.members ?? []).map((r) => [keyText(r), r]));
  const value = (k: LineKey, f: Field) => records.get(keyText(k))?.[f];
  return (
    <div className="scroll">
      <table className="dt mg-diff">
        <thead>
          <tr>
            <th>{t("merge.field")}</th>
            {props.members.map(({ key, merged }) => {
              const k = keyText(key);
              const isTarget = !!props.target && same(key, props.target);
              return (
                <th key={k}>
                  <div className="hstack" style={{ gap: 6 }}>
                    <Radio
                      size="xs"
                      aria-label={t("merge.makeTarget", { key: k })}
                      checked={isTarget}
                      onChange={() => props.onTarget(key)}
                    />
                    <RemovableChip
                      tone={isTarget ? "g" : undefined}
                      removeLabel={t("merge.remove", { key: k })}
                      onRemove={() => props.onRemove(key)}
                    >
                      <span className="mono">{k}</span>
                      {isTarget && ` · ${t("merge.target")}`}
                    </RemovableChip>
                    {!isTarget && (
                      <Checkbox
                        size="xs"
                        aria-label={t("merge.include", { key: k })}
                        checked={merged}
                        onChange={() => props.onToggle(key)}
                      />
                    )}
                  </div>
                </th>
              );
            })}
            <th>{t("merge.result")}</th>
          </tr>
        </thead>
        <tbody>
          {FIELDS.map((f) => {
            const conflict = plan?.conflicts.includes(f);
            const result = plan?.result[f];
            const holders = props.members.filter((m) => !blank(value(m.key, f))).length;
            const picked = props.choices[f];
            const chosen = picked ?? props.members.find((m) => records.has(keyText(m.key)) && value(m.key, f) === result)?.key;
            const cls = f === "pedigre" ? "mono sm" : undefined;
            return (
              <tr key={f} className={conflict ? "mg-conflict" : undefined}>
                <td className="mu">{t(f === "pedigre" ? "register.pedigree" : `register.field.${f}`)}</td>
                {props.members.map(({ key }) => {
                  const v = value(key, f);
                  return (
                    <td key={keyText(key)} className={cls}>
                      {conflict && records.has(keyText(key)) ? (
                        <Radio
                          size="xs"
                          name={f}
                          label={shown(v)}
                          checked={!!chosen && same(chosen, key)}
                          onChange={() => props.onChoose(f, key)}
                        />
                      ) : (
                        shown(v)
                      )}
                    </td>
                  );
                })}
                <td className={cls}>
                  <b>{shown(result)}</b>
                  {holders === 1 && props.members.length > 1 && !blank(result) && <span className="sm mu"> {t("merge.singleValue")}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AffectedList({ affected, pedigrees, removed }: { affected: Affected[]; pedigrees: number; removed: number }) {
  const { t } = useTranslation();
  return (
    <div className="stack">
      {affected
        .filter((a) => a.rows)
        .map((a) => (
          <div key={a.table} className="mg-imp">
            <span>
              {t(`merge.tables.${a.table}`)}
              {a.first_year != null && ` · ${a.first_year === a.last_year ? a.first_year : `${a.first_year}–${a.last_year}`}`}
            </span>
            <b>{formatNumber(a.rows)}</b>
          </div>
        ))}
      <div className="mg-imp">
        <span>{t("merge.pedigrees")}</span>
        <b>{formatNumber(pedigrees)}</b>
      </div>
      <div className="mg-imp">
        <span>{t("merge.combinations")}</span>
        <b>{formatNumber(removed)}</b>
      </div>
    </div>
  );
}

// --- a list of old → new keys ----------------------------------------------------------------------------------------
function ListModal({ opened, onClose, query: initialQuery }: { opened: boolean; onClose: () => void; query: number | null }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [query, setQuery] = useState<number | null>(initialQuery);
  // biome-ignore lint/correctness/useExhaustiveDependencies: opening takes the side panel's query, once
  useEffect(() => {
    if (opened && initialQuery != null) setQuery(initialQuery);
  }, [opened]);
  const lines = useMergeQueryLines({ query: query ?? 0 }, { query: { enabled: opened && query != null } });
  const invalidate = useInvalidateDuplicates();
  const body = text.trim() ? { text } : null;
  // a long list is a whole plan per preview: asked for after a longer pause
  const preview = usePreview("merge-list-preview", body, (b, signal) => mergeListPreview(b, { signal }), 800);
  const plan = preview.data;
  const columns = useMemo<Column<ListRow>[]>(() => {
    const line = (id: "old" | "new", name: (r: ListRow) => string | null): Column<ListRow> => ({
      id,
      header: t(`merge.${id}`),
      value: (r) => keyText(r[id]),
      text: (r) => `${keyText(r[id])} ${name(r) ?? ""}`,
      cell: (r) => (
        <>
          <span className="mono">{keyText(r[id])}</span> <span className="mu">{name(r)}</span>
        </>
      ),
    });
    return [
      codeColumn<ListRow>("row", t("merge.row"), (r) => r.line),
      line("old", (r) => r.old_name),
      line("new", (r) => r.new_name),
      {
        id: "result",
        header: t("merge.result"),
        value: (r) => (r.creates ? t("merge.newId") : t("merge.merge")),
        cell: (r) => <span className={`chip ${r.creates ? "b" : "y"}`}>{r.creates ? t("merge.newId") : t("merge.merge")}</span>,
      },
    ];
  }, [t]);
  const table = useDataTable("genealogy.merge.list", columns, plan?.rows);
  const run = useMergeListRun({
    mutation: {
      onSuccess: (out) => {
        notifications.show({ color: "green", message: t("merge.listDone", { count: out.written }) });
        setText("");
        invalidate();
        onClose();
      },
    },
  });
  return (
    <Modal opened={opened} onClose={onClose} title={t("merge.listTitle")} size="xl">
      <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
      <div className="stack" style={{ gap: 12 }}>
        <span className="sm mu">{t("merge.listHelp")}</span>
        <Textarea
          classNames={{ input: "mono" }}
          label={t("merge.list")}
          autosize
          minRows={4}
          maxRows={10}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          data-autofocus
        />
        <div className="hstack" style={{ gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <FileButton accept=".csv,.txt,text/plain,text/csv" onChange={(f) => void f?.text().then(setText)}>
            {(p) => (
              <Button size="compact-sm" variant="default" {...p}>
                {t("merge.loadFile")}
              </Button>
            )}
          </FileButton>
          <SavedQuerySelect value={query} onChange={(id) => setQuery(id)} label={t("merge.listFromQuery")} w={280} />
          <Button
            size="compact-sm"
            variant="default"
            disabled={!lines.data?.lines.length}
            loading={lines.isFetching}
            onClick={() => lines.data && setText(queryListText(text, lines.data.lines, t("merge.listHeader")))}
          >
            {t("merge.listAppend")}
          </Button>
        </div>
        {query != null && <QueryFeedback busy={false} error={lines.error} retry={lines.refetch} />}
        {query != null && <span className="sm mu">{t("merge.listFromQueryHelp")}</span>}
        {plan && plan.problems.length > 0 && (
          <div className="note warn sm">
            <b>{t("merge.errorRows")}:</b> {plan.problems.map((p) => `${p.line}. ${t(`merge.problem.${p.code}`)}`).join(" · ")}
          </div>
        )}
        {plan && plan.rows.length > 0 && (
          <>
            <div className="stack" style={{ maxHeight: 320 }}>
              <DataTable table={table} rowKey={(r) => `${keyText(r.old)}→${keyText(r.new)}`} />
            </div>
            <AffectedList affected={plan.affected} pedigrees={plan.pedigrees.length} removed={plan.removed.length} />
          </>
        )}
        <div className="hstack" style={{ justifyContent: "flex-end", gap: 8 }}>
          <Button variant="default" onClick={onClose}>
            {t("shell.cancel")}
          </Button>
          <Button
            color="red"
            variant="outline"
            disabled={!plan?.rows.length || plan.problems.length > 0}
            loading={run.isPending}
            onClick={() => body && plan && run.mutate({ data: { ...body, token: plan.token } })}
          >
            {t("merge.listRun", { count: plan?.rows.length ?? 0 })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
